import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle, Loader2 } from "lucide-react";
import { authAPI } from "../../services/api";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    setLoading(true);
    try {
      await authAPI.forgotPassword(email.trim());
      setSubmitted(true);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cyber-50 cyber-grid-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <a
            href="https://shinobi.milot.io/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Go to shinobi.milot.io"
            className="inline-block transition-transform hover:scale-105"
          >
            <img
              src="/logos/wasabi-shinobi-mark-256.png"
              alt="Wasabi Shinobi"
              className="w-20 h-20 mx-auto"
            />
          </a>
        </div>
        <div className="cyber-card rounded-lg p-8 shadow-2xl">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-slate-500 hover:text-slate-800 mb-6"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to login
          </Link>

        {submitted ? (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-neon-green/10 border border-neon-green/40 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-neon-green" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Check your email</h2>
            <p className="text-sm text-slate-500 leading-relaxed">
              If an account exists for <span className="text-slate-800">{email}</span>,
              a reset link has been sent. The link expires in 24 hours.
            </p>
            <p className="text-xs text-gray-600 mt-6">
              Didn't get it? Check spam, or{" "}
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                className="text-shinobi-pink hover:text-shinobi-pink-light underline"
              >
                try again
              </button>
              .
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold mb-2 text-slate-900">
                Reset Password
              </h1>
              <p className="text-sm text-gray-500">
                Enter your email and we'll send you a reset link.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-sm text-red-700">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2"
                >
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    className="terminal-input w-full pl-10 pr-4 py-3 rounded focus:ring-2 focus:ring-shinobi-purple"
                    placeholder="you@company.com"
                    disabled={loading}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-cyber w-full py-3 rounded font-semibold text-sm uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Sending…
                  </>
                ) : (
                  "Send Reset Link"
                )}
              </button>
            </form>
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
